/**
 * FlowHub - useClientProducts Hook
 * TanStack Query hooks for managing client product contracts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { db, functions, supabase } from '../../../shared/lib/supabase';
import { logger } from '../../../shared/lib/logger';

/**
 * Product contract status interface
 */
export interface ProductContractStatus {
  isContracted: boolean;
  isFreeProduct: boolean;
  hasActiveSubscription: boolean;
  hasPendingPayment: boolean;
  contractCount: number;
  canContract: boolean;
  blockReason?: string;
  requiresConfirmation: boolean;
  confirmationMessage?: string;
}

/**
 * Hook to check product contract status for a specific product
 */
export function useProductContractStatus(productId: string | null, clientId: string | null) {
  return useQuery<ProductContractStatus>({
    queryKey: ['product-contract-status', productId, clientId],
    enabled: !!productId && !!clientId,
    queryFn: async () => {
      if (!productId || !clientId) {
        return {
          isContracted: false,
          isFreeProduct: false,
          hasActiveSubscription: false,
          hasPendingPayment: false,
          contractCount: 0,
          canContract: true,
          requiresConfirmation: false,
        };
      }

      // 1. Fetch product info to check if FREE
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('id, monthly_price, setup_fee, name')
        .eq('id', productId)
        .single();

      if (productError) {
        logger.error('Error fetching product', productError);
        throw productError;
      }

      const isFreeProduct = (Number(product?.monthly_price || 0) + Number(product?.setup_fee || 0)) <= 0;

      // 2. Check existing contracts for this product
      const { data: contracts, error: contractsError } = await supabase
        .from('client_products')
        .select('id, is_active')
        .eq('client_id', clientId)
        .eq('product_id', productId);

      if (contractsError) {
        logger.error('Error fetching contracts', contractsError);
        throw contractsError;
      }

      const contractCount = contracts?.length || 0;
      const isContracted = contractCount > 0;

      // 3. Check active subscriptions
      const { data: subscriptions, error: subsError } = await supabase
        .from('client_subscriptions')
        .select('id, status, payment_status')
        .eq('user_id', clientId)
        .eq('product_id', productId)
        .in('status', ['active', 'pending']);

      if (subsError) {
        logger.error('Error fetching subscriptions', subsError);
      }

      const hasActiveSubscription = (subscriptions?.length || 0) > 0;

      // 4. For PAID products, check pending payments via subscription only
      // Note: We rely on backend (contract-product) to check Asaas API directly
      // Here we only check if there's a subscription with pending payment_status
      let hasPendingPayment = false;
      
      if (!isFreeProduct) {
        const { data: pendingSubPayments } = await supabase
          .from('client_subscriptions')
          .select('id, payment_status')
          .eq('user_id', clientId)
          .eq('product_id', productId)
          .in('status', ['active', 'pending'])
          .eq('payment_status', 'pending');

        hasPendingPayment = (pendingSubPayments?.length || 0) > 0;
      }

      // 5. Check for cancelled subscriptions (for FREE products 24h rule)
      const { data: cancelledSubscription } = await supabase
        .from('client_subscriptions')
        .select('cancelled_at')
        .eq('user_id', clientId)
        .eq('product_id', productId)
        .eq('status', 'cancelled')
        .order('cancelled_at', { ascending: false })
        .limit(1)
        .single();

      let hoursSinceCancellation: number | null = null;
      if (cancelledSubscription?.cancelled_at) {
        const cancelledAt = new Date(cancelledSubscription.cancelled_at);
        const now = new Date();
        hoursSinceCancellation = (now.getTime() - cancelledAt.getTime()) / (1000 * 60 * 60);
      }

      // 6. Determine if can contract and messages
      let canContract = true;
      let blockReason: string | undefined;
      let requiresConfirmation = false;
      let confirmationMessage: string | undefined;

      if (isFreeProduct && isContracted) {
        // Check 24h rule for FREE products
        if (hoursSinceCancellation !== null && hoursSinceCancellation < 24) {
          const hoursRemaining = Math.ceil(24 - hoursSinceCancellation);
          canContract = false;
          blockReason = `Este produto gratuito foi cancelado recentemente. Você poderá contratar novamente em ${hoursRemaining} hora(s).`;
        } else if (hoursSinceCancellation === null) {
          // Never cancelled, just exists - block completely
          canContract = false;
          blockReason = 'Este produto gratuito já foi contratado. Não é possível contratar novamente.';
        }
        // If more than 24h since cancellation, can contract (canContract stays true)
      } else if (!isFreeProduct) {
        // PAID products - check conditions
        if (hasPendingPayment) {
          canContract = false;
          blockReason = 'Há um pagamento pendente para este produto. Regularize antes de contratar novamente.';
        } else if (hasActiveSubscription) {
          canContract = true;
          requiresConfirmation = true;
          confirmationMessage = 'Você já possui uma assinatura ativa deste produto. Deseja contratar novamente? Uma nova cobrança será gerada.';
        }
      }

      return {
        isContracted,
        isFreeProduct,
        hasActiveSubscription,
        hasPendingPayment,
        contractCount,
        canContract,
        blockReason,
        requiresConfirmation,
        confirmationMessage,
      };
    },
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch client's contracted products
 */
export function useClientProducts(clientId?: string | null) {
  return useQuery({
    queryKey: ['client-products', clientId],
    enabled: !!clientId,
    retry: 1,
    staleTime: 30000,
    queryFn: async () => {
      if (!clientId) return [];

      let { data, error } = await supabase
        .from('client_products_with_subscription')
        .select('*, product:products(*, n8n_instance:n8n_instances(*))')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) {
        const isMissingViewError = (error?.code === 'PGRST205') || (error?.message && (error.message.includes("Could not find the table 'public.client_products_with_subscription'") || error.message.includes("Could not find table 'public.client_products_with_subscription'")));
        if (isMissingViewError) {
          const { data: fallback, error: fallbackError } = await supabase
            .from('client_products')
            .select('*, product:products(*, n8n_instance:n8n_instances(*))')
            .eq('client_id', clientId)
            .order('created_at', { ascending: false });
          if (fallbackError) throw fallbackError;
          return fallback || [];
        }
        throw error;
      }

      return data || [];
    },
  });
}

/**
 * Hook to contract a new product
 */
export function useContractProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, config, confirmedRecontract }: { productId: string; config?: Record<string, any>; confirmedRecontract?: boolean }) => {
      // Call edge function to create contract and log activity
      const result = await functions.contractProduct({
        product_id: productId,
        config: config || {},
        confirmed_recontract: confirmedRecontract,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to contract product');
      }

      const clientProduct = result.client_product_id;

      // Clone workflow via Edge Function
      try {
        const cloneResult = await functions.cloneWorkflow(productId, clientProduct, config || {});
        
        if (!cloneResult.success) {
          throw new Error(cloneResult.error || 'Failed to clone workflow');
        }

        // If this is a template (not custom) product, ensure we got a cloned_workflow_id back.
        // If there's no cloned_workflow_id, treat as a failure and delete the created client_product.
        if ((cloneResult.product_type !== 'custom') && !cloneResult.cloned_workflow_id) {
          logger.error('Clone workflow returned no cloned_workflow_id', null, cloneResult);
          await db.clientProducts.delete(clientProduct);
          throw new Error('Falha ao clonar workflow: cloned_workflow_id ausente');
        }

        // If this is a custom product, ensure the response contains custom_workflow_id
        if (cloneResult.product_type === 'custom' && !cloneResult.custom_workflow_id) {
          logger.error('Clone workflow returned no custom_workflow_id', null, cloneResult);
          await db.clientProducts.delete(clientProduct);
          throw new Error('Falha ao clonar workflow customizado: custom_workflow_id ausente');
        }
      } catch (error) {
        logger.error('Error cloning workflow', error);
        // Delete the client_product record if workflow cloning failed
        await db.clientProducts.delete(clientProduct);
        throw new Error(`Falha ao clonar workflow: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      }

      return { id: clientProduct };
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['client-products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-contracted-count'] });
    },
  });
}

/**
 * Hook to update client product configuration
 */
export function useUpdateClientProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, config }: { id: string; config: Record<string, any> }) => {
      console.log('Updating product config:', { id, config });

      try {
        // Update workflow configuration in n8n via edge function
        const result = await functions.updateWorkflowConfig(id, config);
        console.log('Update config result:', result);

        if (!result.success) {
          throw new Error(result.error || 'Failed to update configuration');
        }

        return result;
      } catch (error) {
        logger.error('Error updating config', error);
        throw new Error(`Falha ao atualizar configuração: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-products'] });
    },
  });
}

/**
 * Hook to toggle client product active status
 */
export function useToggleClientProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      // Toggle workflow in n8n via edge function
      try {
        console.log('Calling toggle-workflow edge function...', { id, isActive });
        const result = await functions.toggleWorkflow(id, isActive);
        console.log('Toggle workflow result:', result);
        
        if (!result.success) {
          // Extract user-friendly message from error response
          const errorData = result as any;
          
          // Determine the specific error type and provide appropriate message
          if (errorData.error === 'workflow_has_issues') {
            const message = errorData.message || 'O workflow possui erros no n8n. Corrija antes de ativar.';
            const affectedNodes = errorData.affected_nodes || [];
            
            // If there are affected nodes, include them in the error
            if (affectedNodes.length > 0) {
              throw new Error(`${message}\n\nNós afetados: ${affectedNodes.join(', ')}`);
            }
            throw new Error(message);
          }
          
          if (errorData.error === 'workflow_credentials_missing') {
            throw new Error(errorData.message || 'O workflow necessita de credenciais para ser ativado.');
          }
          
          if (errorData.error === 'workflow_no_trigger') {
            throw new Error(errorData.message || 'O workflow não possui gatilhos configurados.');
          }

          if (errorData.error === 'workflow_fetch_failed') {
            throw new Error(errorData.message || 'Não foi possível validar o workflow no n8n.');
          }

          if (errorData.error === 'workflow_validation_error') {
            throw new Error(errorData.message || 'Erro ao validar o workflow.');
          }
          
          // Generic error with user-friendly message
          if (errorData.user_friendly && errorData.message) {
            throw new Error(errorData.message);
          }
          
          throw new Error(result.error || 'Falha ao alterar status do workflow');
        }
        
        return result;
      } catch (error: any) {
        logger.error('Error toggling workflow', error);
        
        // If it's already a formatted error, throw it as-is
        if (error.message) {
          throw error;
        }
        
        // Otherwise create a generic error
        throw new Error(`Não foi possível ${isActive ? 'ativar' : 'desativar'} o workflow. Tente novamente ou contate o administrador.`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-products'] });
    },
  });
}

/**
 * Hook to uncontract (delete) a client product
 */
export function useUncontractProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Before attempting deletion in n8n, fetch the client product to determine whether
      // there is a cloned_workflow_id. We MUST NOT delete custom or template workflows.
      const { data: clientProduct, error: cpErr } = await db.clientProducts.getById(id);
      if (cpErr) {
        logger.warn('Could not fetch client product before uncontract', cpErr);
      }

      // Only attempt to delete workflows that were cloned (cloned_workflow_id present)
      const hasClonedWorkflow = clientProduct?.cloned_workflow_id;
      if (hasClonedWorkflow) {
        try {
          console.log('Deleting cloned workflow from n8n...');
          await functions.deleteWorkflow(id);
          console.log('Cloned workflow deleted from n8n');
        } catch (error) {
          logger.error('Error deleting workflow from n8n', error);
          // Continue even if workflow deletion fails
        }
      } else {
        // No cloned workflow to delete
      }

      // Delete client product record
      const { error } = await db.clientProducts.delete(id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-products'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-contracted-count'] });
    },
  });
}
